import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuthStore, type UserCompanyRole } from '@stores/authStore'
import { authApi } from '@services/api'

/**
 * STORY-1033: Switch the active Mandant — server first, then locally.
 *
 * The backend resolves the tenant of Quality Gate, AI extraction & Co. from
 * `users.active_company_id`. A selection kept only in the browser therefore
 * changes the label in the title bar but none of the data, so the switch is
 * persisted via `PUT /api/auth/me/active-company` and adopted locally only
 * after the server confirmed it. All tenant-scoped queries are invalidated so
 * the new Mandant shows up without a page reload.
 */
export function useCompanySwitch() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const setActiveCompany = useAuthStore((s) => s.setActiveCompany)
  const activeCompanies = useAuthStore((s) => s.activeCompanies)
  const user = useAuthStore((s) => s.user)
  const [pendingCompanyId, setPendingCompanyId] = useState<number | null>(null)

  const activeCompanyId = activeCompanies[0]?.company_id
  const serverCompanyId = user?.active_company_id

  /** Returns true when the Mandant is active afterwards (already or newly). */
  const switchCompany = useCallback(
    async (company: UserCompanyRole): Promise<boolean> => {
      if (pendingCompanyId !== null) return false
      // Only a no-op when the local selection AND users.active_company_id
      // already point at this Mandant. Sessions from before STORY-1033 have a
      // Mandant persisted in localStorage while the server still resolves a
      // different one — clicking the seemingly active entry must repair that.
      if (company.company_id === activeCompanyId && company.company_id === serverCompanyId) {
        return true
      }

      setPendingCompanyId(company.company_id)
      try {
        await authApi.setActiveCompany(company.company_id)
        setActiveCompany(company)
        void queryClient.invalidateQueries()
        return true
      } catch {
        // Keep the previous selection — never show a Mandant the server
        // does not filter by (e.g. 403 for an inaccessible company)
        toast.error(t('company.switchError', 'Mandant konnte nicht gewechselt werden'))
        return false
      } finally {
        setPendingCompanyId(null)
      }
    },
    [activeCompanyId, serverCompanyId, pendingCompanyId, queryClient, setActiveCompany, t]
  )

  return { switchCompany, pendingCompanyId }
}

export default useCompanySwitch
